import RenovationHouseForm from 'components/calculators/RenovationHouseForm';
import { useCallback, useMemo } from 'react';
import { cloneDeep, omit, pick, uniq } from 'lodash';
import { FormattedMessage } from 'react-intl';
import { toast } from 'react-toastify';
import useUpdateProjectRenovationHouseMutation from 'hooks/project-details/useUpdateProjectRenovationHouseMutation';
import useGetCO2Calculations from 'hooks/co2-calculator/useCO2Calculations';
import LoadingOverlay from 'components/common/LoadingOverlay';
import { isNetworkError, messageOf } from 'utils/error';
import type {
  IRenovationHouseFormValues,
  IRenovationHouseSubmitArgs,
} from 'components/calculators/RenovationHouseForm/types';
import type { ProjectQuery, CO2CalculationsQuery } from 'graphql/generated/graphql';
import requireValue from 'utils/requireValue';
import {
  initialValuesCO2Calculator,
  whitelistedProps,
  whitelistedPropsCO2Calculator,
} from './RenovationHouseCalculator.data';
import { hasOtherInvestmentsAmount, rawOf, toFeatureFlags } from './utils';

type TProject = ProjectQuery['project'];

/**
 * The values the page sends (the project fields, without the CO2 calculator fields): the enabled
 * feature names and the step. All fields of the mutation input are optional.
 */
interface IRenovationHouseFormData {
  features: string[];
  step?: number | null;
  other_investments_amount?: number | null;
  other_investments_comment?: string | null;
  number_of_extra_large_windows?: number | null;
  [field: string]: unknown;
}

/** The project fields picked by `whitelistedProps` (the form values only have the picked fields). */
interface IPickedRenovationProject {
  name?: string;
  zip?: string | null;
  form_values?: Record<string, unknown>;
}

interface IRenovationHouseCalculatorProps {
  project: TProject;
}

const handleSpecificCategories = (_formData: IRenovationHouseFormData) => {
  const formData = cloneDeep(_formData);

  if (!formData.features.includes('other_investments')) {
    formData.other_investments_amount = null;
    formData.other_investments_comment = '';
  }

  if (formData.features.includes('renovation_house_new_windows')) {
    if (!Number.isFinite(formData.number_of_extra_large_windows)) {
      formData.number_of_extra_large_windows = 0;
    }
  }

  return formData;
};

const useSubmitRenovationHouseDetailsCalculator = ({ projectId }: { projectId: string }) => {
  const updateProjectRenovationHouseMutation = useUpdateProjectRenovationHouseMutation();

  const onSubmit = useCallback(
    async ({
      values,
      formikActions,
      isEditMode,
      currentStep,
      nextStep,
      lastStep,
    }: IRenovationHouseSubmitArgs) => {
      const { setSubmitting } = formikActions;
      // The initial values always have the features (`getInitialValues`).
      const features = Object.entries(requireValue(values.features, 'features')).reduce<string[]>(
        (arr, [feature, isEnabled]) => {
          if (isEnabled) arr.push(feature);
          return arr;
        },
        [],
      );

      const formData: IRenovationHouseFormData = {
        ...cloneDeep(omit(values, whitelistedPropsCO2Calculator)),
        features: uniq([...features]),
      };

      // Number(): `currentStep + 1` as before (StepWizard always passes currentStep).
      const step = isEditMode ? null : Number(currentStep) + 1;
      formData.step = step;

      const _formData = handleSpecificCategories(formData);
      setSubmitting(true);
      try {
        await updateProjectRenovationHouseMutation(projectId, _formData);
        if (isEditMode && typeof lastStep === 'function') {
          lastStep();
        } else if (typeof nextStep === 'function') {
          nextStep();
        }
      } catch (err) {
        const error = isNetworkError(rawOf(err)) ? (
          <FormattedMessage id="errors.no_server_response" />
        ) : (
          messageOf(err)
        );
        toast.error(error);
      } finally {
        setSubmitting(false);
      }
    },
    [projectId, updateProjectRenovationHouseMutation],
  );

  return onSubmit;
};

const getInitialValues = (
  project: TProject,
  co2Calculations: CO2CalculationsQuery['co2Calculations'] | undefined,
): IRenovationHouseFormValues => {
  if (!project) return {};
  const { form_values, ...whitelisted }: IPickedRenovationProject = pick(project, whitelistedProps);
  const co2EmissionsFormValues = pick(
    co2Calculations?.co2_emissions_before?.form_values,
    whitelistedPropsCO2Calculator,
  );

  const restFeatures: Record<string, boolean> = {};
  if (project.progress.step !== null) {
    restFeatures.reserves_for_unexpected_costs = true;
    restFeatures.renovation_house_energy_certificate = true;
  }
  restFeatures.other_investments = hasOtherInvestmentsAmount(project.form_values);

  const initialValues = {
    ...initialValuesCO2Calculator,
    ...whitelisted,
    ...form_values,
    ...(co2EmissionsFormValues ? { ...co2EmissionsFormValues } : {}),
    features: {
      ...toFeatureFlags(requireValue(project.features, 'reduce')),
      ...restFeatures,
    },
  };
  return initialValues;
};

const RenovationHouseCalculator = ({ project }: IRenovationHouseCalculatorProps) => {
  const onSubmit = useSubmitRenovationHouseDetailsCalculator({ projectId: project._id });
  const [co2Calculations, loading] = useGetCO2Calculations(project._id);
  const initialValues = useMemo(
    () => getInitialValues(project, co2Calculations),
    [co2Calculations, project],
  );

  if (loading) return <LoadingOverlay />;

  return (
    <RenovationHouseForm
      onSubmit={onSubmit}
      initialValues={initialValues}
      isEditMode={project.progress.step === null}
      step={project.progress.step}
    />
  );
};

export default RenovationHouseCalculator;
